/**
 * HistoricalActiveView — time-machine view mounted by the game screen
 * when the user has tapped an event row in the activity feed.
 *
 * Layout mirrors the spectator branch (activity feed alongside the
 * map) so the user can keep scrubbing through events by tapping
 * other rows. The map renders the reconstructed historical
 * territories; a full-width banner above the map and activity feed indicates
 * review mode and offers a "back to live" tap target.
 *
 * Map taps are inert in this view — territory press handlers are
 * deliberately not wired so the user can't accidentally place /
 * attack / fortify against a frozen historical snapshot.
 */

import React from 'react'
import { View, Text, TouchableOpacity, Platform } from 'react-native'
import { MaterialCommunityIcons } from '@expo/vector-icons'
import { useTheme } from '../../hooks/useTheme'
import { MapView } from '../map/MapView'
import { usePortraitMapReserves } from '../../hooks/usePortraitMapReserves'
import { usePortraitMapFit } from '../../hooks/useGameMapChrome'
import { useMapDimensions } from '../../lib/useMapDimensions'
import { desktopCanvasBottomSpace } from '../../lib/mapDimensions'
import { MapControlsBarSlot } from '../map/MapControlsBar'
import { RecentActivitySidebar } from './RecentActivitySidebar'
import { GAME_TOP_BAR_HEIGHT, PORTRAIT_GAME_TOP_BAR_PAD } from '../../lib/gameChrome'
import { getEventHighlight } from '../../lib/historicalState'
import { groupEvents } from '../../lib/gameLog'
import { logStepTargets } from '../../lib/logSelection'
import { isTextEntryTarget, snapshotArrowTarget, SNAPSHOT_ARROW_KEYS } from '../../lib/snapshotEscape'
import { PLAYER_COLORS, clampPlayerSlot } from '@warcarp/ui'
import { buildColorBySlot } from '../../lib/playerColor'
import type { GameState } from '../../lib/gameState'
import type { GameResponse } from '@warcarp/api-types'
import type { PanOffset } from '../../lib/mapZoom'
import type { AttackAnimationEntry } from '../../hooks/useAttackEvents'
import type { ConquestAnimationEntry } from '../../hooks/useConquestEvents'
import type { FortifyAnimationEntry } from '../../hooks/useFortifyEvents'

/**
 * Everything the snapshot needs to run the regions inspector. Built by the
 * caller (ActiveGameView) from the same state the LIVE regions view uses, so
 * both views share one selection — switch to snapshot with a region focused and
 * it stays focused.
 *
 * `panel` is the RegionDetailsPanel node itself rather than its inputs: the
 * panel takes a game state, a roster and four callbacks, and threading all of
 * that through a second component just to re-assemble it here would be a second
 * place for the two views to drift apart.
 */
export interface SnapshotRegionsMode {
  panel: React.ReactNode
  focusedRegionId: string | null
  focusedDetailTerritoryId: string | null
  detailAdjacentIds: string[]
  regionColorById: Record<string, string>
  onTerritoryPress: (territoryId: string) => void
  onBackgroundPress: () => void
}

export interface HistoricalActiveViewProps {
  id: string
  historicalGameState: GameState
  historicalEventIndex: number
  totalEvents: number
  game: GameResponse
  myPlayer: { slot: number } | undefined | null
  isLandscape: boolean
  isDesktop: boolean
  onScrubEvent: (eventIndex: number) => void
  onReturnToLive: () => void
  /** Desktop/web arrow keys step the log (the game screen is focused and no
   *  drawer, sheet or modal is open on top). */
  arrowKeysEnabled?: boolean
  regionToggleNode: React.ReactNode
  regionToggleCorner: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
  zoomToggleNode: React.ReactNode
  zoomToggleCorner: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
  /** MapControlsBar rendered directly beneath the snapshot map (reset/+/− zoom
   *  + Game|Regions toggle). Replaces the old floating zoom / region chips. */
  mapControlsBar?: React.ReactNode
  /** Zoom + pan state lives on the parent game screen so toggling
   *  the magnifying glass / dragging the snapshot map works while
   *  in review mode. Wire identical props to the live MapView in
   *  the parent. */
  zoomEnabled?: boolean
  panOffset?: PanOffset
  onPanChange?: (next: PanOffset) => void
  zoomScale?: number
  onZoomScaleChange?: (scale: number) => void
  /** Region-bonus overlay state. When true, MapView paints the
   *  per-region badges + tints. Lets the region toggle chip work
   *  in snapshot mode the same way it does in live play. */
  showRegionBonuses?: boolean
  /**
   * Non-null exactly while the Game|Regions toggle is on Regions. When set, the
   * snapshot switches to a full regions inspector: the event log steps aside for
   * the region list and the map becomes tappable for inspection.
   *
   * Toggling to Regions used to only paint bonus badges over the snapshot — the
   * log stayed put, there was no region list, and tapping a region did nothing,
   * so "Regions" in snapshot was a strictly worse version of the same view you
   * get in live play. The snapshot itself is unaffected: the board is still the
   * frozen historical one, and returning to Game restores the selected event
   * row exactly as it was (the log is hidden, not unmounted — see below).
   */
  regionsMode?: SnapshotRegionsMode | null
  /**
   * Maximum rows in the snapshot sidebar. The sidebar groups the FULL `/events`
   * log (carried on `historicalGameState.events`) so its `eventIndex` values are
   * already absolute — reconstruction is correct at any index regardless of this
   * cap. The cap only bounds how many recent rows render (this view is a quick
   * peek from the live feed; the master /log is the full virtualized scrubber).
   */
  maxEvents?: number
}

export function HistoricalActiveView({
  id,
  historicalGameState,
  historicalEventIndex,
  totalEvents,
  game,
  myPlayer,
  isLandscape,
  isDesktop,
  onScrubEvent,
  onReturnToLive,
  arrowKeysEnabled = false,
  regionToggleNode,
  regionToggleCorner,
  zoomToggleNode,
  zoomToggleCorner,
  mapControlsBar,
  zoomEnabled = false,
  panOffset: panOffsetProp,
  onPanChange,
  zoomScale,
  onZoomScaleChange,
  showRegionBonuses = false,
  regionsMode = null,
  maxEvents = 80,
}: HistoricalActiveViewProps) {
  // Use the parent's pan offset when supplied so the magnifying-
  // glass toggle and drag gesture work in review mode. Falling back
  // to the origin keeps the older callers (with no zoom thread) at
  // their previous no-zoom behaviour.
  const panOffset: PanOffset = panOffsetProp ?? { tx: 0, ty: 0 }
  const handlePanChange = onPanChange ?? (() => {})

  // Seat slot → render color hex, honoring each player's chosen (decoupled)
  // color. Muted grey for neutral / unresolved (slot ≤ 0).
  const colorBySlot = buildColorBySlot(game.players)
  const colorHexForSlot = (slot: number) =>
    slot > 0 ? PLAYER_COLORS[clampPlayerSlot(colorBySlot[slot] ?? slot)]?.hex ?? '#888' : '#888'

  // Derive territory highlight + frozen attack badge from the
  // snapshot event. The MapView consumes:
  //   - selectedTerritoryId  (primary highlight, amber)
  //   - secondaryTerritoryId (secondary highlight, also amber)
  //   - previewTargetIds     (rings the troop number of every touched cell)
  //   - staticAttackBadge    (frozen "−N, −M" pair for attack events)
  const events = historicalGameState.events ?? []
  const highlight = getEventHighlight(events, historicalEventIndex)
  const staticAttackBadge: AttackAnimationEntry | undefined = (() => {
    if (!highlight?.attackSnapshot) return undefined
    const a = highlight.attackSnapshot
    // Slot string → PLAYER_COLORS index. Neutral defender uses a
    // muted grey so the −0 badge still reads against the ocean.
    const attackerSlot = parseInt(a.attackerPlayerId, 10)
    const defenderSlot = a.defenderPlayerId === '' ? 0 : parseInt(a.defenderPlayerId, 10)
    const attackerColor = colorHexForSlot(attackerSlot)
    const defenderColor = colorHexForSlot(defenderSlot)
    return {
      attackerTerritoryId: a.fromTerritoryId,
      defenderTerritoryId: a.toTerritoryId,
      attackerLost: a.attackerLost,
      defenderLost: a.defenderLost,
      attackerColor,
      defenderColor,
      conquered: a.conquered,
      startedAt: 0,
    }
  })()

  const staticConquestRing: ConquestAnimationEntry | undefined = (() => {
    const r = highlight?.conquestRing
    if (!r) return undefined
    const slot = parseInt(r.ownerPlayerId, 10)
    const ownerColor = colorHexForSlot(slot)
    return { territoryId: r.territoryId, ownerColor, startedAt: 0 }
  })()

  const staticFortifyEntry: FortifyAnimationEntry | undefined = (() => {
    const f = highlight?.fortifySnapshot
    if (!f) return undefined
    const slot = parseInt(f.playerId, 10)
    const playerColor = colorHexForSlot(slot)
    return {
      fromTerritoryId: f.fromTerritoryId,
      toTerritoryId: f.toTerritoryId,
      armies: f.armies,
      playerColor,
      startedAt: 0,
    }
  })()

  // One opaque full-width status strip above both the map and activity sidebar.
  // On overlapping maps it masks the board beneath this retained header area;
  // the underlying map keeps exactly the live view's position and fit.
  //
  // The "viewing event N of M — read-only" copy is gone too — it
  // could overflow on narrow screens. The Snapshot tag + Back-to-live
  // button speak for themselves.
  // Same height as the game top bar it replaces (incl. its 2px rule), so the
  // map below doesn't shift when entering / leaving the snapshot.
  const portraitReserves = usePortraitMapReserves(historicalGameState.map)
  const { fit: portraitFit } = usePortraitMapFit(historicalGameState.map.id)
  // Previous / next walk the same rows the snapshot log shows (it groups this
  // same full log), so a step equals tapping the neighbouring row. Row indices
  // don't depend on names, hence the identity resolvers.
  const logRows = React.useMemo(
    () => groupEvents(historicalGameState.events ?? [], (id) => id, (id) => id),
    [historicalGameState.events],
  )
  const step = logStepTargets(logRows, historicalEventIndex)
  // Arrow keys mirror the Newer / Older buttons. Read through a ref so the one
  // window listener always sees the current step without re-subscribing.
  const arrowState = React.useRef({ step, enabled: arrowKeysEnabled, onScrubEvent })
  arrowState.current = { step, enabled: arrowKeysEnabled, onScrubEvent }
  React.useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return
    const onKey = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return
      const { step: current, enabled, onScrubEvent: scrub } = arrowState.current
      const modified = event.altKey || event.ctrlKey || event.metaKey || event.shiftKey
      const textEntryFocused = isTextEntryTarget(document.activeElement as HTMLElement | null)
      const target = snapshotArrowTarget({ key: event.key, modified, enabled, textEntryFocused, step: current })
      // A handled arrow, including a clamped no-op at either end, must not
      // scroll the page or the log underneath.
      if (enabled && !modified && !textEntryFocused && SNAPSHOT_ARROW_KEYS.has(event.key)) event.preventDefault()
      if (target !== null) scrub(target)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])
  // The log lists newest first, so "up the log" is newer: Newer carries an up
  // chevron and sits on the left (ArrowUp / ArrowLeft), Older a down chevron
  // on the right (ArrowDown / ArrowRight).
  const { theme } = useTheme()
  const bloodHex = theme === 'dark' ? '#e0463f' : '#a01818'
  const stepButton = (target: number | null, label: string, icon: 'chevron-up' | 'chevron-down', a11y: string, testID: string) => (
    <TouchableOpacity
      testID={testID}
      onPress={target === null ? undefined : () => onScrubEvent(target)}
      disabled={target === null}
      accessibilityLabel={a11y}
      accessibilityRole="button"
      accessibilityState={{ disabled: target === null }}
      hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
      className="border border-blood rounded-lg pl-1 pr-2 py-0.5 flex-row items-center"
      style={{ opacity: target === null ? 0.35 : 1, gap: 2 }}
    >
      <MaterialCommunityIcons name={icon} size={14} color={bloodHex} accessible={false} />
      <Text className="text-blood font-stencil text-[10px] uppercase tracking-widest">{label}</Text>
    </TouchableOpacity>
  )
  const banner = (
    <View
      testID="snapshot-status-bar"
      className="bg-paper border-b-2 border-blood flex-row items-center justify-between px-3"
      style={{ zIndex: 10, height: isLandscape ? GAME_TOP_BAR_HEIGHT : portraitReserves.topBarHeight, paddingTop: isLandscape ? 0 : PORTRAIT_GAME_TOP_BAR_PAD, flexShrink: 0, alignSelf: 'stretch' }}
    >
      {stepButton(step.next, 'Newer', 'chevron-up', 'Newer event', 'snapshot-newer')}
      <View className="flex-row items-center" style={{ gap: 10 }}>
        <Text className="text-blood font-stencil text-[10px] uppercase tracking-widest">
          Snapshot
        </Text>
        <TouchableOpacity
          onPress={onReturnToLive}
          accessibilityLabel="Return to live game"
          accessibilityRole="button"
          className="bg-blood rounded-lg px-2 py-0.5"
        >
          <Text className="text-white font-stencil text-[10px] uppercase tracking-widest">
            Back to live
          </Text>
        </TouchableOpacity>
      </View>
      {stepButton(step.previous, 'Older', 'chevron-down', 'Older event', 'snapshot-older')}
    </View>
  )

  // Map highlight props. In regions mode the region inspector owns the map:
  // the selection is whatever region/territory the user tapped, not the frozen
  // event's territories, and every territory is tappable so they can walk the
  // board. Otherwise the snapshot's event highlight drives it, as before.
  const mapHighlightProps = regionsMode
    ? {
        regionFocusId: regionsMode.focusedRegionId,
        selectedRegionId: regionsMode.focusedRegionId ?? undefined,
        selectedRegionColor: regionsMode.focusedRegionId
          ? regionsMode.regionColorById[regionsMode.focusedRegionId]
          : undefined,
        selectedTerritoryId: regionsMode.focusedDetailTerritoryId ?? undefined,
        adjacentTerritoryIds: regionsMode.detailAdjacentIds,
        onTerritoryPress: regionsMode.onTerritoryPress,
        onBackgroundPress: regionsMode.onBackgroundPress,
        allTerritoriesInteractive: true,
      }
    : {
        selectedTerritoryId: highlight?.primaryId,
        secondaryTerritoryId: highlight?.secondaryId,
        // Ring the troop number of every territory this event touched (a
        // placement across three cells rings all three). Instant, not faded —
        // the whole board just jumped to a frozen moment, so a fade would
        // trail the change it exists to announce.
        previewTargetIds: highlight?.haloIds,
        previewTargetsInstant: true,
        selectedFillMode: highlight?.fillMode ?? ('lighten' as const),
      }

  /**
   * The panel beside/below the map. In regions mode the event log is HIDDEN,
   * not unmounted — its scroll position and the selected row's highlight are
   * local to the mounted ScrollView, so unmounting it would send the user back
   * to the top of the log with nothing selected when they toggled back to Game.
   * The requirement is that returning to Game lands you exactly where you were.
   */
  // Same shorter desktop canvas as the live map (GameMapHost), measured with
  // the same reserves, so the board doesn't move entering the snapshot.
  const snapshotDims = useMapDimensions(historicalGameState, {
    reservedBottom: mapControlsBar ? 44 : undefined,
    reservedTop: GAME_TOP_BAR_HEIGHT,
  })
  const snapshotBottomSpace = desktopCanvasBottomSpace(snapshotDims, historicalGameState.map.viewBox)

  const sidePanel = (orientation: 'sidebar' | 'bottom-panel') => (
    <>
      <View
        style={regionsMode ? { display: 'none' } : undefined}
        // The wrapper must not change the sidebar's box. Portrait: `flex-1`, so
        // the bottom panel keeps filling the space under the map (the sidebar's
        // own flex-1 then fills this). Landscape: `flex-row`, so the sidebar —
        // which sets its own WIDTH and relies on being stretched to full height
        // by its parent — stretches on this wrapper's cross axis exactly as it
        // did when it was a direct child of the outer row.
        className={orientation === 'bottom-panel' ? 'flex-1' : 'flex-row'}
      >
        <RecentActivitySidebar
          gameId={id}
          state={historicalGameState}
          players={game.players}
          {...(orientation === 'bottom-panel' ? { orientation: 'bottom-panel' as const } : {})}
          onViewAtEvent={onScrubEvent}
          hideHeader
          selectedEventIndex={historicalEventIndex}
          maxEvents={maxEvents}
        />
      </View>
      {regionsMode?.panel}
    </>
  )

  return (
    <View className="flex-1">
      {banner}
    <View className={isLandscape ? 'flex-1 flex-row' : 'flex-1'}>
      {isLandscape && sidePanel('sidebar')}
      {isLandscape ? (
        <View className="flex-1">
          {/* Map column. Top-aligned (not centered) so the map sits
              flush against the snapshot banner — no gap above. The
              bg-paper-up / dark:bg-paper pair matches the MapView's
              own ocean palette in light + dark theme respectively,
              so any vertical slack below the SVG (from aspect-fit
              letterboxing on landscape phones with a different
              aspect than the map) blends seamlessly with the
              ocean inside the SVG. Reported on Brave Android
              landscape; iOS Safari happened to have a matching
              aspect that hid the gap. */}
          {/* Desktop: sized to the map (not flex-1), so the controls bar sits
              right under it exactly as in the live view — nothing moves when
              entering or leaving a snapshot. */}
          <View className={`${isDesktop ? '' : 'flex-1 '}items-start justify-start bg-paper-up dark:bg-paper`}>
          <MapView
            state={historicalGameState}
            viewerSlot={myPlayer?.slot ?? 1}
            regionToggleNode={regionToggleNode}
            regionToggleCorner={regionToggleCorner}
            zoomToggleNode={zoomToggleNode}
            zoomToggleCorner={zoomToggleCorner}
            zoomEnabled={zoomEnabled}
            hideOnMapZoomButtons={!!mapControlsBar}
            panOffset={panOffset}
            onPanChange={handlePanChange}
            zoomScale={zoomScale}
            onZoomScaleChange={onZoomScaleChange}
            showRegionBonuses={showRegionBonuses}
            {...mapHighlightProps}
            reservedBottom={(mapControlsBar ? 44 : 0) + snapshotBottomSpace}
            // The banner spans the top at the top bar's height: size the map
            // for the pane below it (the live wide layout reserves the same
            // strip, so the map doesn't move entering or leaving a snapshot).
            reservedTop={GAME_TOP_BAR_HEIGHT}
            staticAttackBadge={staticAttackBadge}
            staticConquestRing={staticConquestRing}
            staticFortifyEntry={staticFortifyEntry}
          />
          </View>
          <MapControlsBarSlot state={historicalGameState} fullWidth>{mapControlsBar}</MapControlsBarSlot>
        </View>
      ) : (
        <View style={{ marginTop: -portraitReserves.topOverlap }}>
          <View style={{ transformOrigin: 'top', transform: [{ scale: portraitFit.scale }] }}>
          <MapView
            state={historicalGameState}
            viewerSlot={myPlayer?.slot ?? 1}
            regionToggleNode={regionToggleNode}
            regionToggleCorner={regionToggleCorner}
            zoomToggleNode={zoomToggleNode}
            zoomToggleCorner={zoomToggleCorner}
            zoomEnabled={zoomEnabled}
            hideOnMapZoomButtons={!!mapControlsBar}
            panOffset={panOffset}
            onPanChange={handlePanChange}
            zoomScale={zoomScale}
            onZoomScaleChange={onZoomScaleChange}
            showRegionBonuses={showRegionBonuses}
            {...mapHighlightProps}
            // Same sizing as the live map (shared reserves + top-margin trim):
            // the banner replaces the top bar at the same height, so entering
            // a snapshot keeps the board's size and place. The log's flex-1
            // fills whatever is left below.
            reservedTop={portraitReserves.reservedTop}
            reservedBottom={portraitReserves.reservedBottom}
            trimMargins
            staticAttackBadge={staticAttackBadge}
            staticConquestRing={staticConquestRing}
            staticFortifyEntry={staticFortifyEntry}
          />
          </View>
          <View style={{ marginTop: portraitFit.tipsHeight - portraitFit.shift }}>
            <MapControlsBarSlot state={historicalGameState} fullWidth>{mapControlsBar}</MapControlsBarSlot>
          </View>
        </View>
      )}
      {!isLandscape && sidePanel('bottom-panel')}
    </View>
    </View>
  )
}
